import { useDeviceSize } from '@atlassian/learning-components';

/**
 * Custom hook that combines device size detection with breakpoint utilities.
 * Provides an easy way to make responsive decisions in your React components.
 *
 * Mobile: ≤ 767px
 * Tablet: 768px - 1279px
 * Desktop: ≥ 1280px
 *
 * @example
 * const { isMobile, isTablet, isDesktop } = useBreakpoint();
 *
 * if (isMobile) {
 *   return <MobileView />;
 * }
 *
 * @example
 * const { deviceWidth, currentBreakpoint } = useBreakpoint();
 * console.log(`Current device: ${currentBreakpoint} at ${deviceWidth}px`);
 */

const BREAKPOINTS_VALUES = {
  mobile: 768,
  desktop: 1280,
} as const;

export interface UseBreakpointReturn {
  deviceWidth: number;
  currentBreakpoint: 'mobile' | 'tablet' | 'desktop';
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

export const useBreakpoint = (): UseBreakpointReturn => {
  const deviceWidth = useDeviceSize();

  const isMobileDevice = deviceWidth < BREAKPOINTS_VALUES.mobile;
  const isTabletDevice = deviceWidth >= BREAKPOINTS_VALUES.mobile && deviceWidth < BREAKPOINTS_VALUES.desktop;
  const isDesktopDevice = deviceWidth >= BREAKPOINTS_VALUES.desktop;

  const getCurrentBreakpoint = (): 'mobile' | 'tablet' | 'desktop' => {
    if (isDesktopDevice) {
      return 'desktop';
    }
    if (isTabletDevice) {
      return 'tablet';
    }

    return 'mobile';
  };

  return {
    deviceWidth,
    currentBreakpoint: getCurrentBreakpoint(),
    isMobile: isMobileDevice,
    isTablet: isTabletDevice,
    isDesktop: isDesktopDevice,
  };
};
